import { Button, Flex, Form, Input, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineArrowLeft, AiOutlineMail } from "react-icons/ai";
import { Link } from "react-router";

import { QueryErrorAlert } from "design-system/components/QueryErrorAlert";
import { useScreenSize } from "design-system/hooks";
import { useSandboxAuth } from "sandbox/contexts/SandboxAuthContext";
import { sandboxPaths } from "sandbox/paths";
import { checkEmailDomain } from "sandbox/utils/emailValidation";

import s from "./ForgotPasswordScreen.module.css";

interface FormValues {
  email: string;
}

export const ForgotPasswordScreen = () => {
  const { t } = useTranslation(["sandbox", "common"]);
  const { requestPasswordReset } = useSandboxAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState("");
  const size = useScreenSize();
  const showHero = !["xs", "sm"].includes(size);

  const onFinish = async (values: FormValues) => {
    setSubmitting(true);
    setError("");
    try {
      await requestPasswordReset(values.email);
      setSentTo(values.email);
    } catch (e) {
      setError(
        (e as { message?: string })?.message ?? "An unexpected error occurred",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Flex vertical className={s.formContent}>
        {sentTo ? (
          <>
            <div className={s.iconBadge} aria-hidden>
              <AiOutlineMail />
            </div>
            <Typography.Title level={2} className={s.heading}>
              {t("components.forgotPasswordScreen.checkEmail.title")}
            </Typography.Title>
            <Typography.Paragraph className={s.subtitle}>
              {t("components.forgotPasswordScreen.checkEmail.description", {
                email: sentTo,
              })}
            </Typography.Paragraph>
            <div className={s.footer}>
              <Link to={sandboxPaths.login} className={s.backLink}>
                <AiOutlineArrowLeft /> {t("actions.backToSignIn")}
              </Link>
            </div>
          </>
        ) : (
          <>
            <Typography.Title level={2} className={s.heading}>
              {t("components.forgotPasswordScreen.form.title")}
            </Typography.Title>
            <Typography.Paragraph className={s.subtitle}>
              {t("components.forgotPasswordScreen.form.description")}
            </Typography.Paragraph>

            {error && (
              <div className={s.errorAlert}>
                <QueryErrorAlert description={error} />
              </div>
            )}

            <Form
              layout="vertical"
              onFinish={onFinish}
              requiredMark={false}
              autoComplete="on"
              className={s.form}
            >
              <Form.Item
                name="email"
                label={t("labels.workEmail")}
                rules={[
                  {
                    required: true,
                    message: t("validation.required", {
                      field: t("labels.workEmail"),
                    }),
                  },
                  { type: "email", message: t("validation.emailType") },
                  {
                    validator: (_, value: string | undefined) => {
                      if (!value) return Promise.resolve();
                      const result = checkEmailDomain(value);
                      return result.code === "ok"
                        ? Promise.resolve()
                        : Promise.reject(new Error(result.message));
                    },
                  },
                ]}
              >
                <Input size="large" placeholder={t("placeholders.workEmail")} />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={submitting}
                className={s.submit}
              >
                {submitting
                  ? t("actions.sendingLink")
                  : t("actions.sendResetLink")}
              </Button>
            </Form>

            <Flex
              justify="center"
              align="center"
              gap="small"
              className={s.footer}
            >
              <Typography.Text className={s.footerText}>
                {t("auth.newToThredd")}
              </Typography.Text>
              <Link
                to={`${sandboxPaths.login}?mode=signup`}
                className={s.footerLink}
              >
                {t("components.authScreen.createAccountLink")}
              </Link>
            </Flex>
          </>
        )}
      </Flex>

      {showHero && (
        <Flex align="center" justify="center" className={s.heroPanel}>
          <img
            src="/images/cardCreatedSuccessfully.png"
            alt=""
            className={s.heroImage}
          />
        </Flex>
      )}
    </>
  );
};
